import { z } from 'zod';
import { BodySlotSchema, ItemInstanceSchema } from '../data/items.js';
import { MAX_BAG_SIZE, WARDROBE_FIRST_SLOT } from '../rules/inventory.js';
/**
 * Inventory traffic. The full inventory only ever travels to its owner;
 * intents are validated here and applied by the server inside a transaction.
 */
const BagSlotSchema = z
    .number()
    .int()
    .min(0)
    .max(MAX_BAG_SIZE - 1);
export const InventoryStateSchema = z.object({
    /** Slots of this bag; the server checks every move against it. */
    bagSize: z.number().int().min(1).max(MAX_BAG_SIZE),
    bag: z.array(z.object({ slot: BagSlotSchema, item: ItemInstanceSchema })),
    /**
     * The wardrobe (ADR 0072): cosmetics and companions, as many as owned, in
     * the order they came; no slot to move them between.
     */
    wardrobe: z
        .array(z.object({ slot: z.number().int().min(WARDROBE_FIRST_SLOT), item: ItemInstanceSchema }))
        .default([]),
    equipped: z.partialRecord(BodySlotSchema, ItemInstanceSchema),
    /** The owner's gold (ADR 0016); travels with the inventory, to its owner only. */
    gold: z.number().int().min(0).default(0),
});
export const EquipMessageSchema = z.object({ itemId: z.uuid() });
export const UnequipMessageSchema = z.object({ slot: BodySlotSchema });
export const MoveItemMessageSchema = z
    .object({ from: BagSlotSchema, to: BagSlotSchema })
    .refine((move) => move.from !== move.to, 'Same slot');
/** Tidy the bag (its « Ranger » button): nothing to say, the server decides the order. */
export const SortBagMessageSchema = z.object({}).strict().optional();
/**
 * Destroy a bag item for good (ADR 0084): the whole stack without `quantity`.
 * Never a worn piece nor a look of the wardrobe.
 */
export const DestroyItemMessageSchema = z.object({
    itemId: z.uuid(),
    quantity: z.number().int().min(1).optional(),
});
export const UseItemMessageSchema = z.object({ itemId: z.uuid() });
export const INVENTORY_REFUSALS = [
    'unknown-item',
    'not-equipment',
    'level-too-low',
    'wrong-class',
    'bag-full',
    'not-usable',
    /** A healing potion at full health: it stays in the bag. */
    'full-health',
    'invalid',
    /** A quest's bait (ADR 0047): no quest wants it now, not by a den here, or its catch still runs. */
    'bait-not-now',
    'bait-here',
    'bait-busy',
    /** The bag is on a trade table (ADR 0034): it waits until the trade ends. */
    'busy',
    /** Lent by a quest under way (ADR 0084): it goes back with the quest, never to nothing. */
    'not-destroyable',
];
export const InventoryRejectedSchema = z.object({ reason: z.enum(INVENTORY_REFUSALS) });
//# sourceMappingURL=inventory.js.map